import styled from '@emotion/styled';
import { makeStyles } from '@material-ui/core/styles';
import { MessageSquare, Send, ThumbsDown, ThumbsUp } from 'lucide-react';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ActionPreviewCard, type ActionPreview } from './ActionPreviewCard';
import { ThinkingDots } from './ThinkingDots';

export interface QuickReply {
  label: string;
  /** First chip = gradient (primary), rest = outlined (secondary). */
  variant?: 'primary' | 'secondary';
}

/** Per-message feedback state, owned by ChatWindow and passed down
 *  (ChatMessage is controlled — holds no feedback state of its own). */
export interface MessageFeedbackState {
  rating?: 'up' | 'down';
  commentOpen?: boolean;
  commentSubmitted?: boolean;
  submitting?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
  },
  userRow: {
    justifyContent: 'flex-end',
  },
  assistantRow: {
    justifyContent: 'flex-start',
  },
  userBubble: {
    maxWidth: '75%',
    padding: '9px 14px',
    // Kinked bottom-right corner per design — anchors user message visually
    // to the right rail.
    borderRadius: '10px 10px 2px 10px',
    backgroundColor: '#0E78E4',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 300,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  markdown: {
    fontSize: 13.5,
    fontWeight: 300,
    lineHeight: 1.6,
    wordBreak: 'break-word',
    '& p': { margin: '0 0 8px 0', '&:last-child': { marginBottom: 0 } },
    '& ul, & ol': { margin: '4px 0', paddingLeft: 20 },
    '& li': { marginBottom: 2 },
    '& code': {
      backgroundColor: 'rgba(0,0,0,0.06)',
      borderRadius: 3,
      padding: '1px 4px',
      fontSize: 13,
    },
    '& pre': {
      backgroundColor: 'rgba(0,0,0,0.06)',
      borderRadius: 4,
      padding: 8,
      overflowX: 'auto',
      '& code': { backgroundColor: 'transparent', padding: 0 },
    },
    '& strong': { fontWeight: 600 },
    '& h1, & h2, & h3': { fontSize: 14, fontWeight: 600, margin: '8px 0 4px' },
    '& table': { borderCollapse: 'collapse', width: '100%', margin: '8px 0' },
    '& th, & td': { border: '1px solid rgba(0,0,0,0.12)', padding: '4px 8px', fontSize: 13 },
    '& th': { fontWeight: 600, backgroundColor: 'rgba(0,0,0,0.04)' },
  },
}));

// Gradient left-border (3px) on AI bubbles: outer wrap is the gradient,
// inner is white. Asymmetric corner radii (outer 3/8/8/3, inner 1/7/7/1)
// keep the border looking like a continuous edge.
const AssistantColumn = styled('div')`
  max-width: 92%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AssistantOuter = styled('div')`
  padding-left: 3px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  border-radius: 3px 8px 8px 3px;
`;

const AssistantInner = styled('div')`
  background: #fff;
  border-radius: 1px 7px 7px 1px;
  padding: 12px 14px;
  color: #272c30;
`;

// One row holding the feedback icons (left) + quick-reply chips (right).
const ControlsRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const QuickReplyRow = styled('div')`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

// Subtle, Facebook-style feedback icons. Neutral gray, darken on hover,
// tinted when selected (up=blue, down=red, comment-open=purple).
const MessageFeedbackRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const FeedbackIconButton = styled('button')<{
  tone: 'up' | 'down' | 'comment';
  selected?: boolean;
}>`
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: color 120ms ease, background 120ms ease;
  color: ${(p) =>
    p.selected
      ? p.tone === 'up'
        ? '#0E78E4'
        : p.tone === 'down'
        ? '#E5484D'
        : '#7856FF'
      : '#9AA0A6'};

  &:hover:not(:disabled) {
    color: ${(p) =>
      p.tone === 'up' ? '#0E78E4' : p.tone === 'down' ? '#E5484D' : '#7856FF'};
    background: #f0f5fa;
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(14, 120, 228, 0.3);
  }
`;

// Inline comment box (thumbs-down follow-up / standalone comment). Plain
// styled textarea + send button — NOT MUI TextField — so it ports across
// MUI v4 (verifiable) and v9 (react-19) without rowsMax/maxRows divergence.
const CommentWrap = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-top: 4px;
`;

const CommentTextarea = styled('textarea')`
  flex: 1;
  min-height: 34px;
  max-height: 96px;
  resize: vertical;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #dde0e3;
  border: none;
  outline: none;
  padding: 7px 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  color: #272c30;

  &:focus {
    box-shadow:
      inset 0 0 0 1px #0e78e4,
      0 0 0 3px rgba(14, 120, 228, 0.18);
  }
`;

const CommentSend = styled('button')`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background: ${(p) => p.theme.dado.copilot.gradientDiagonal};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: filter 120ms ease, opacity 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentThanks = styled('div')`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 300;
  color: #5f676e;
`;

/** Inline comment editor — holds its own transient text so keystrokes
 *  don't churn the parent's feedback-state map. */
const InlineCommentBox: React.FC<{
  submitted?: boolean;
  onSubmit: (text: string) => void;
}> = ({ submitted, onSubmit }) => {
  const [text, setText] = useState('');
  if (submitted) {
    return <CommentThanks>Thanks for the detail.</CommentThanks>;
  }
  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSubmit(t);
  };
  return (
    <CommentWrap>
      <CommentTextarea
        placeholder="What could be better? (optional)"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        rows={1}
      />
      <CommentSend
        type="button"
        onClick={send}
        disabled={!text.trim()}
        aria-label="Send comment"
      >
        <Send size={13} />
      </CommentSend>
    </CommentWrap>
  );
};

const QuickReplyChip = styled('button')<{ variant: 'primary' | 'secondary' }>`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: filter 120ms ease;

  ${(p) =>
    p.variant === 'primary'
      ? `
    background: ${p.theme.dado.copilot.gradientHorizontal};
    color: #fff;
    font-weight: 600;
    &:hover { filter: brightness(1.05); }
  `
      : `
    background: transparent;
    box-shadow: inset 0 0 0 1px #DDE0E3;
    color: #5F676E;
    font-weight: 300;
    &:hover { background: #F0F5FA; }
  `}

  &:focus-visible {
    outline: none;
    box-shadow:
      ${(p) =>
        p.variant === 'primary'
          ? '0 0 0 3px rgba(14,120,228,0.25)'
          : 'inset 0 0 0 1px #0E78E4, 0 0 0 3px rgba(14,120,228,0.25)'};
  }
`;

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  isLoading?: boolean;
  /** Quick-reply chips shown beneath the AI bubble. Only honored for
   *  assistant messages that aren't a parsed action result. */
  quickReplies?: QuickReply[];
  onQuickReply?: (text: string) => void;
  /** Per-message feedback (controlled). When `showFeedback` is true the
   *  thumbs/comment row renders under the assistant bubble. */
  showFeedback?: boolean;
  feedback?: MessageFeedbackState;
  onRate?: (rating: 'up' | 'down') => void;
  onToggleComment?: () => void;
  onSubmitComment?: (text: string) => void;
}

function tryParseAction(content: string): ActionPreview | null {
  try {
    const data = JSON.parse(content);
    if (data.created && data.taskId) return { type: 'task_created', data };
    if (data.created && data.blockId) return { type: 'subtask_created', data };
    if (data.created && data.messageId) return { type: 'message_created', data };
    if (data.created && data.templateId) return { type: 'template_created', data };
    if (data.imported && data.newTemplateId) return { type: 'template_imported', data };
    if (data.imported && data.newTaskId) return { type: 'task_imported', data };
    if (data.updated && data.taskId) return { type: 'task_updated', data };
    if (data.updated && data.messageId) return { type: 'message_updated', data };
    if (data.created && data.actorKey) return { type: 'actor_added', data };
    return null;
  } catch {
    return null;
  }
}

/** True when an assistant message's content is a structured write-action
 *  result (template/task/etc. created). Used by ChatWindow to suppress the
 *  "Build it"/"Adjust" chips once the plan has been implemented. */
export function messageIsWriteAction(content: string): boolean {
  return tryParseAction(content) !== null;
}

export const ChatMessage: React.FC<React.PropsWithChildren<ChatMessageProps>> = ({
  role,
  content,
  isLoading,
  quickReplies,
  onQuickReply,
  showFeedback,
  feedback,
  onRate,
  onToggleComment,
  onSubmitComment,
}) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <div className={`${classes.root} ${classes.assistantRow}`}>
        <ThinkingDots />
      </div>
    );
  }

  const action = role === 'assistant' ? tryParseAction(content) : null;
  if (action) {
    return (
      <div className={`${classes.root} ${classes.assistantRow}`}>
        <ActionPreviewCard preview={action} confirmed />
      </div>
    );
  }

  if (role === 'user') {
    return (
      <div className={`${classes.root} ${classes.userRow}`}>
        <div className={classes.userBubble}>{content}</div>
      </div>
    );
  }

  // Don't render an empty assistant bubble. The agent's intermediate
  // tool-call turns persist with empty content; without this guard they
  // show as blank boxes (made worse by the feedback row). Loading + action
  // results are handled above, so anything reaching here with no text is
  // a non-renderable turn.
  if (!content || !content.trim()) {
    return null;
  }

  const hasQuickReplies = !!(quickReplies && quickReplies.length > 0 && onQuickReply);
  const showFeedbackRow = !!showFeedback;
  const submitting = !!feedback?.submitting;

  return (
    <div className={`${classes.root} ${classes.assistantRow}`}>
      <AssistantColumn>
        <AssistantOuter>
          <AssistantInner>
            <div className={classes.markdown}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </AssistantInner>
        </AssistantOuter>
        {(showFeedbackRow || hasQuickReplies) && (
          <ControlsRow>
            {showFeedbackRow && (
              <MessageFeedbackRow>
                <FeedbackIconButton
                  type="button"
                  tone="up"
                  selected={feedback?.rating === 'up'}
                  disabled={submitting}
                  onClick={() => onRate?.('up')}
                  aria-label="Good response"
                  title="Good response"
                >
                  <ThumbsUp size={15} />
                </FeedbackIconButton>
                <FeedbackIconButton
                  type="button"
                  tone="down"
                  selected={feedback?.rating === 'down'}
                  disabled={submitting}
                  onClick={() => onRate?.('down')}
                  aria-label="Bad response"
                  title="Bad response"
                >
                  <ThumbsDown size={15} />
                </FeedbackIconButton>
                <FeedbackIconButton
                  type="button"
                  tone="comment"
                  selected={!!feedback?.commentOpen}
                  onClick={() => onToggleComment?.()}
                  aria-label="Add a comment"
                  title="Add a comment"
                >
                  <MessageSquare size={15} />
                </FeedbackIconButton>
              </MessageFeedbackRow>
            )}
            {hasQuickReplies && (
              <QuickReplyRow>
                {quickReplies!.map((reply, idx) => (
                  <QuickReplyChip
                    key={reply.label}
                    type="button"
                    variant={reply.variant ?? (idx === 0 ? 'primary' : 'secondary')}
                    onClick={() => onQuickReply!(reply.label)}
                  >
                    {reply.label}
                  </QuickReplyChip>
                ))}
              </QuickReplyRow>
            )}
          </ControlsRow>
        )}
        {showFeedbackRow && feedback?.commentOpen && (
          <InlineCommentBox
            submitted={feedback?.commentSubmitted}
            onSubmit={(t) => onSubmitComment?.(t)}
          />
        )}
      </AssistantColumn>
    </div>
  );
};
